import React, { useState, useEffect } from "react";
import { fetchJson } from "../../api";

const GOLD = "#d4a550";
const GOLD_DIM = "rgba(212,165,80,0.15)";
const GOLD_BORDER = "rgba(212,165,80,0.35)";
const BORDER = "rgba(255,255,255,0.07)";
const TEXT = "#e8e6df";
const TEXT_DIM = "rgba(232,230,223,0.45)";

export default function ParallelismiPanel({ planId, episodes, lanes, onClose, onSaved }) {
  // Ricava le fasi uniche dalle lanes
  const phases = [...new Map(lanes.map(l => [l.phaseId, { id: l.phaseId, name: l.phaseName }])).values()];
  
  // Calcola il max lines attuale per fase da lanes
  const maxLinesByPhase = new Map();
  lanes.forEach(l => {
    const cur = maxLinesByPhase.get(l.phaseId) || 0;
    if (l.lineNo > cur) maxLinesByPhase.set(l.phaseId, l.lineNo);
  });

  // Stato parallelismi: key = `${episodeNo}_${phaseId}` → value
  const [par, setPar] = useState({});
  const [saving, setSaving] = useState(false);

  // Carica episodePhaseConfig dal backend
  useEffect(() => {
    fetchJson(`/postplans/${planId}`)
      .then(({ episodePhaseConfig = [], phases: phList = [] }) => {
        const init = {};
        episodePhaseConfig.forEach(c => {
          init[`${c.episode_no}_${c.phase_id}`] = c.parallel_lines;
        });
        // Default 1 per quelli mancanti
        episodes.forEach(ep => {
          phases.forEach(ph => {
            const key = `${ep.episodeNo}_${ph.id}`;
            if (!init[key]) init[key] = maxLinesByPhase.get(ph.id) || 1;
          });
        });
        setPar(init);
      })
      .catch(() => {});
  }, [planId]);

  function getVal(ep, ph) {
    return par[`${ep.episodeNo}_${ph.id}`] ?? 1;
  }

  function setVal(ep, ph, v) {
    const max = maxLinesByPhase.get(ph.id) || 1;
    const val = Math.max(1, Math.min(max, parseInt(v) || 1));
    setPar(p => ({ ...p, [`${ep.episodeNo}_${ph.id}`]: val }));
  }

  function applyToAll(ph, v) {
    const max = maxLinesByPhase.get(ph.id) || 1;
    const val = Math.max(1, Math.min(max, parseInt(v) || 1));
    const next = { ...par };
    episodes.forEach(ep => { next[`${ep.episodeNo}_${ph.id}`] = val; });
    setPar(next);
  }

  async function handleSave() {
    setSaving(true);
    try {
      await fetchJson(`/postplans/${planId}/parallelismi`, {
        method: "POST",
        body: JSON.stringify({ parallelismi: par })
      });
      onSaved();
    } catch(e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  const cellStyle = {
    background: "rgba(255,255,255,0.03)",
    border: `1px solid ${BORDER}`,
    borderRadius: 4,
    color: TEXT,
    fontSize: 13,
    padding: "4px 6px",
    width: 52,
    textAlign: "center",
  };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:"#0a0a0f",border:`1px solid ${GOLD_BORDER}`,borderRadius:12,padding:"24px 28px",maxWidth:"90vw",maxHeight:"85vh",display:"flex",flexDirection:"column",gap:16,overflow:"hidden" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <span style={{ fontWeight:700,fontSize:15,color:GOLD }}>Modifica parallelismi</span>
          <button onClick={onClose} style={{ background:"none",border:"none",color:TEXT_DIM,cursor:"pointer",fontSize:18 }}>×</button>
        </div>
        <div style={{ fontSize:12,color:TEXT_DIM }}>Le modifiche verranno applicate e il piano verrà ricalcolato.</div>
        <div style={{ overflowX:"auto",overflowY:"auto", scrollbarWidth:"thin", scrollbarColor:"rgba(212,165,80,0.4) transparent",flex:1 }}>
          <table style={{ borderCollapse:"collapse",fontSize:12,minWidth:"100%" }}>
            <thead>
              <tr style={{ background:"#12121a" }}>
                <th style={{ padding:"8px 12px",textAlign:"left",color:TEXT_DIM,fontWeight:600,borderBottom:`1px solid ${BORDER}`,minWidth:60 }}>Ep.</th>
                {phases.map(ph => (
                  <th key={ph.id} style={{ padding:"8px 12px",textAlign:"center",color:TEXT_DIM,fontWeight:600,borderBottom:`1px solid ${BORDER}`,minWidth:100 }}>
                    {ph.name}
                    <div style={{ fontSize:10,color:TEXT_DIM,fontWeight:400 }}>max {maxLinesByPhase.get(ph.id)||1}</div>
                  </th>
                ))}
              </tr>
              <tr style={{ background:"rgba(212,165,80,0.04)" }}>
                <td style={{ padding:"6px 12px",color:GOLD,fontSize:11,fontWeight:700 }}>↓ Tutti</td>
                {phases.map(ph => (
                  <td key={ph.id} style={{ padding:"6px 12px",textAlign:"center" }}>
                    <input type="number" min={1} max={maxLinesByPhase.get(ph.id)||1}
                      defaultValue={1}
                      onChange={e => applyToAll(ph, e.target.value)}
                      style={{ ...cellStyle, border:`1px solid ${GOLD_BORDER}`, background:GOLD_DIM }} />
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {episodes.map((ep, i) => (
                <tr key={ep.episodeNo} style={{ borderBottom:`1px solid ${BORDER}`, background: i%2===0?"transparent":"rgba(255,255,255,0.01)" }}>
                  <td style={{ padding:"6px 12px",color:TEXT_DIM,fontWeight:600 }}>Ep. {ep.episodeNo}</td>
                  {phases.map(ph => (
                    <td key={ph.id} style={{ padding:"6px 12px",textAlign:"center" }}>
                      <input type="number" min={1} max={maxLinesByPhase.get(ph.id)||1}
                        value={getVal(ep, ph)}
                        onChange={e => setVal(ep, ph, e.target.value)}
                        style={cellStyle} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"none",border:`1px solid ${BORDER}`,borderRadius:6,color:TEXT_DIM,cursor:"pointer",padding:"8px 18px",fontSize:13 }}>Annulla</button>
          <button onClick={handleSave} disabled={saving} style={{ background:GOLD_DIM,border:`1px solid ${GOLD_BORDER}`,borderRadius:6,color:GOLD,cursor:"pointer",padding:"8px 18px",fontSize:13,fontWeight:700 }}>
            {saving ? "Salvataggio..." : "Applica e ricalcola"}
          </button>
        </div>
      </div>
    </div>
  );
}
